// FILE THAT CONTAINS INITIAL CONFIG OF NOTIFS

import { t } from '@/config/i18n';
/* HELPERS */
import { getPredictiveModelName } from '@/helpers/pm';
import { getFullnameOrEmail } from '@/helpers/users';
/* TYPES */
import type { MeNotifications } from '@/types/api/me/me';
import type {
  NotificationConfig,
  PredictiveModelTranslatedData,
  TeamAnalysisExportData
} from '@/types/notifications/notification';
import { ASSESSMENTS_CONFIG } from '@/enums/assessments';
import { buildExportNotifAction } from './notificationLayout.helpers.ts';
import { useToast } from '@af-lib/vue-ui';

// Notification color classes
const GLOBAL_COLOR = 'bg-status-informative';
const RECRUITER_COLOR = 'bg-role-recruiter';
const CANDIDATE_COLOR = 'bg-role-candidate';
const EMPLOYEE_COLOR = 'bg-role-employee';
const ERROR_COLOR = 'bg-status-error';

function getAssessmentName(assessmentName?: string): string {
  switch (assessmentName) {
    case 'swipe':
    case 'shape':
      return t(ASSESSMENTS_CONFIG['swipe'].label);
    case 'drive':
      return t(ASSESSMENTS_CONFIG['drive'].label);
    case 'brain':
      return t(ASSESSMENTS_CONFIG['brain'].label);
    case 'voice':
      return t(ASSESSMENTS_CONFIG['voice'].label);
    case 'scan':
      return t(ASSESSMENTS_CONFIG['screening'].label);
    case 'all':
      return t('general.tests.name.all');
    default:
      return assessmentName ?? '';
  }
}

const notifConfig = {
  recruiter_workflow_candidate_completed: {
    class: () => 'fas fa-clipboard-list-check',
    color: () => GLOBAL_COLOR,
    translationKey: notification => {
      const roleName = notification.data?.role_name || '';
      return t('notifications.contacts.workflow_completed', {
        type: roleName ? 'job' : 'other',
        user_fullname: getFullnameOrEmail(notification.data.candidate),
        role_name: roleName ? t(roleName) : ''
      });
    },
    route: notification => {
      return {
        name: 'profile',
        params: { uuid: notification.data.candidate.uuid }
      };
    },
    ariaLabel: () => t('accessibility.notification.redirect.no_campaign')
  },
  assessment_done: {
    class: () => 'fas fa-clipboard-list-check',
    color: () => GLOBAL_COLOR,
    translationKey: notification => {
      return t('notifications.contacts.test_completed', {
        fullname: getFullnameOrEmail(notification.data.candidate, false),
        testname: getAssessmentName(notification.data.assessment?.name)
      });
    },
    route: notification => {
      return {
        name: 'profile',
        params: { uuid: notification.data.candidate.uuid }
      };
    },
    ariaLabel: () => t('accessibility.notification.redirect.no_campaign')
  },
  recruiter_assessment_done: {
    class: () => 'fas fa-clipboard-list-check',
    color: () => GLOBAL_COLOR,
    translationKey: notification => {
      return t('notifications.contacts.test_completed', {
        fullname: getFullnameOrEmail(notification.data.candidate, false),
        testname: getAssessmentName(notification.data.assessment)
      });
    },
    route: notification => {
      return {
        name: 'profile',
        params: { uuid: notification.data.candidate.uuid }
      };
    },
    ariaLabel: () => t('accessibility.notification.redirect.no_campaign')
  },
  recruiter_assessment_not_passed: {
    class: () => 'fas fa-universal-access',
    color: () => CANDIDATE_COLOR,
    translationKey: notification => {
      return t('notifications.recruiter.accessibility.assessment_skipped', {
        user_fullname: getFullnameOrEmail(notification.data.candidate, false),
        assessment: notification.data.assessment,
        user_firstname: notification.data.candidate.first_name
      });
    },
    route: notification => {
      return {
        name: 'profile',
        params: { uuid: notification.data.candidate.uuid }
      };
    },
    ariaLabel: () => t('accessibility.notification.redirect.no_campaign')
  },
  manager_invitation_accepted: {
    class: () => 'fas fa-check',
    color: () => GLOBAL_COLOR,
    translationKey: notification => {
      return t('notifications.invitations.manager_account.accepted', {
        manager_fullname: getFullnameOrEmail(notification.data, false)
      });
    },
    route: () => {
      return { name: 'network' };
    },
    ariaLabel: () => t('accessibility.notification.redirect.manager')
  },
  recruiter_data_policy: {
    class: () => 'fas fa-building-shield',
    color: () => GLOBAL_COLOR,
    translationKey: () => t('notifications.legal.policy.body'),
    link: notification => notification.data.link,
    ariaLabel: () => t('accessibility.notification.redirect.privacy_policy')
  },
  predictive_model_ai_published: {
    class: () => 'fas fa-briefcase-clock',
    color: () => RECRUITER_COLOR,
    translationKey: notification => {
      return t('notifications.predictive_model.is_ready', {
        modelname: notification.data.name
      });
    },
    route: notification => {
      const pmAIUuid: string = notification.data.workshop_uuid;
      return { name: 'view_predictive_model_ai', params: { uuid: pmAIUuid } };
    },
    ariaLabel: () => t('accessibility.notification.redirect.model')
  },
  recruiter_sharepool_accepted: {
    class: () => 'fas fa-check',
    color: () => GLOBAL_COLOR,
    translationKey: notification => {
      return t('notifications.invitations.contact.invitation_accepted', {
        fullname: getFullnameOrEmail(notification.data.candidate)
      });
    },
    route: notification => {
      return {
        name: 'profile',
        params: { uuid: notification.data.candidate.uuid }
      };
    },
    ariaLabel: () => t('accessibility.notification.redirect.no_campaign')
  },
  recruiter_predictive_model_ai_published: {
    class: () => 'fas fa-briefcase-clock',
    color: () => RECRUITER_COLOR,
    translationKey: notification => {
      return t('notifications.predictive_model.is_ready', {
        modelname: notification.data.name
      });
    },
    route: notification => {
      const pmAIUuid: string = notification.data.workshop_uuid;
      return { name: 'view_predictive_model_ai', params: { uuid: pmAIUuid } };
    },
    ariaLabel: () => t('accessibility.notification.redirect.model')
  },
  recruiter_job_offer_created: {
    class: notification => (notification.data.error !== undefined ? 'fas fa-xmark' : 'fas fa-check'),
    color: notification => (notification.data.error !== undefined ? ERROR_COLOR : RECRUITER_COLOR),
    translationKey: notification => {
      // Error cases
      if (notification.data.error !== undefined) {
        switch (notification.data.error) {
          case 432: // Insufficient
            return t('notifications.predictive_model.data', {
              model_name: notification.data.pm_name
            });
          case 433: // Discriminatory
            return t('notifications.predictive_model.data', {
              model_name: notification.data.pm_name
            });
          case 434: // OpenAI Down
            return t('notifications.predictive_model.down', {
              model_name: notification.data.pm_name
            });
          default: // General
            return t('notifications.predictive_model.general_error', {
              model_name: notification.data.pm_name
            });
        }
      }
      // Success case
      return t('notifications.predictive_model.new', {
        model_name: notification.data.pm_name
      });
    },
    route: notification => {
      // Return to PM table if error
      if (notification.data.error !== undefined) {
        return { name: 'predictive_models' };
      }

      const pmAIUuid: string = notification.data.pm_uuid;
      return { name: 'view_predictive_model', params: { uuid: pmAIUuid } };
    },
    ariaLabel: notification =>
      notification.data.error
        ? t('accessibility.notification.redirect.model_view')
        : t('accessibility.notification.redirect.model')
  },
  recruiter_assessment_updated: {
    class: () => 'fas fa-user',
    color: () => RECRUITER_COLOR,
    translationKey: notification => {
      return t('notifications.invitations.contact.test_update', {
        fullname: getFullnameOrEmail(notification.data.candidate, false),
        gender: notification.data.candidate?.gender
      });
    },
    route: notification => {
      return {
        name: 'profile',
        params: { uuid: notification.data.candidate.uuid }
      };
    },
    ariaLabel: () => t('accessibility.notification.redirect.no_campaign')
  },
  // CF
  recruiter_dev_plan_employee_created: {
    class: () => 'fas fa-clipboard',
    color: () => EMPLOYEE_COLOR,
    translationKey: notification => {
      const { first_name, last_name, dev_plan_name } = notification.data;
      return t('notifications.recruiter.development_plan.new', {
        employee_fullname: `${first_name} ${last_name}`,
        plan_name: dev_plan_name
      });
    },
    route: () => {
      return { name: 'competency_framework', query: { tab: 'dev_plans' } };
    },
    ariaLabel: () => t('accessibility.notification.redirect.development_plan')
  },
  recruiter_dev_plan_employee_completed: {
    class: () => 'fas fa-clipboard',
    color: () => EMPLOYEE_COLOR,
    translationKey: notification => {
      const { first_name, last_name, dev_plan_name } = notification.data;
      return t('notifications.recruiter.development_plan.done', {
        employee_fullname: `${first_name} ${last_name}`,
        plan_name: dev_plan_name
      });
    },
    route: () => {
      return { name: 'competency_framework', query: { tab: 'dev_plans' } };
    },
    ariaLabel: () => t('accessibility.notification.redirect.development_plan')
  },
  recruiter_voice_pm_skills_translated: {
    class: () => 'fas fa-business-time',
    color: () => GLOBAL_COLOR,
    translationKey: (notification: MeNotifications<PredictiveModelTranslatedData>) => {
      const { name, type } = notification.data.predictive_model;
      const pmName = getPredictiveModelName(name, type);
      return t('notifications.tests.voice.model_ready', {
        model_name: pmName
      });
    },
    route: (notification: MeNotifications<PredictiveModelTranslatedData>) => {
      let route = 'view_predictive_model';
      if (notification.data.predictive_model.type === 'ai') {
        route = 'view_predictive_model_ai';
      }
      return {
        name: route,
        params: { uuid: notification.data.predictive_model.uuid }
      };
    },
    invalidates: (notification: MeNotifications<PredictiveModelTranslatedData>) => {
      return [['getPredictiveModel', { pmUuid: notification.data.predictive_model.uuid }]];
    },
    ariaLabel: () => t('accessibility.notification.redirect.model')
  },
  exportable_file_generated: {
    class: () => 'fa fa-down-to-line',
    color: () => RECRUITER_COLOR,
    translationKey: () => t('notifications.recruiter.team_analysis.file_ready'),
    ariaLabel: () => t('notifications.recruiter.team_analysis.file_ready'),
    action: {
      label: () => t('general.download'),
      handler: async (notification: MeNotifications<TeamAnalysisExportData>) => {
        buildExportNotifAction(notification);
      }
    },
    onPusherEvent: (
      notification: MeNotifications<TeamAnalysisExportData>,
      deleteNotifCallback: (uuids: string[]) => void
    ) => {
      const toast = useToast();

      toast.show(t('notifications.recruiter.team_analysis.file_ready'), {
        duration: Infinity,
        action: {
          icon: 'fas fa fa-down-to-line',
          label: t('general.download'),
          onClick: () => {
            buildExportNotifAction(notification);
            if (deleteNotifCallback) deleteNotifCallback([notification.uuid]);
          }
        }
      });
    }
  }
};
/**
 * Function that renders initial config for notifs
 * @param {string} type - notification type to get config for
 * @returns {INotificationConfig} object containing notif config
 */
function getNotifConfig(type: string): NotificationConfig {
  return notifConfig[type];
}

export { getNotifConfig };
